import { SLOT_DURATION } from './constants';
import { Series } from './types';

/**
 * Converts an IndexedDB request object into a Promise for easier asynchronous handling.
 *
 * The function takes an IndexedDB request object, attaches event handlers for `onsuccess`
 * and `onerror`, and returns a Promise. The Promise resolves with the result of the request
 * on success, or rejects with an error object if the request encounters an error.
 *
 * @param request - An IndexedDB request object that will be wrapped into a Promise.
 * @returns A Promise that resolves with the result of the request or rejects with the error encountered.
 * @throws An error if the request fails and no specific error is provided in the request object.
 */
export const reqToPromise = (request) =>
  new Promise((resolve, reject) => {
    request.onsuccess = (ev) => resolve(ev.target.result);
    request.onerror = (ev) => reject(ev.target.error || new Error('IDB request error'));
  });

/**
 * Calculates the ending timestamp by adding 360 seconds to the given timestamp.
 *
 * @param timestamp - The starting timestamp in seconds.
 * @returns The resulting timestamp after adding 360 seconds to the input timestamp.
 */
// export const getTimestampEnd = (timestamp: number) => timestamp + 360;

/**
 * Generates a key for a map based on the provided timestamp.
 *
 * @param timestamp The input timestamp used to create the map key.
 * @returns An array containing the start timestamp and the calculated end timestamp.
 */
// export const getMapKey = (timestamp: number): [number, number] => [timestamp, getTimestampEnd(timestamp)];

/**
 * Determines the slot ID based on the given time and a predefined slot duration.
 *
 * @param time The timestamp or time value used to calculate the slot ID in milliseconds.
 * @returns The computed slot ID corresponding to the given time.
 */
export const getSlotId = (time: number) => Math.floor(time / SLOT_DURATION)

/**
 * Calculates the start timestamp of a specific time slot.
 *
 * This function takes a given time value, determines the slot ID it belongs to
 * using the `getSlotId` function, and then calculates the start timestamp of
 * that slot by multiplying the slot ID by the slot duration.
 *
 * @param time The time value for which the start timestamp of the slot is calculated.
 * @returns The timestamp representing the start of the slot in which the given time falls.
 */
export const getSlotStartTimestamp = (time: number) => getSlotId(time) * SLOT_DURATION;

/**
 * Calculates the next slot identifier based on a given timestamp.
 *
 * @param time The timestamp to calculate the next slot from.
 * @returns The identifier for the next slot.
 */
export const getNextSlot = (time: number) => getSlotId(getSlotStartTimestamp(time + SLOT_DURATION));

/**
 * Groups data entries into time slots based on a computed slot identifier.
 *
 * @param data The input dataset containing time-series data for different players.
 * Each player is identified by an ID, and their data is organized by timestamps.
 * @returns An object where keys are slot identifiers, each containing nested player data,
 * with player IDs as keys and their corresponding time-based*/
export const groupByTimeSlots = (data: Series) => {
  const slots = {};
  const playerIds = Object.keys(data);

  for (let i = 0; i < playerIds.length; i++) {
    const playerId = playerIds[i];
    const entries = data[playerId];

    // Processo le entries in batch per ridurre la pressione sulla memoria
    const timeKeys = Object.keys(entries);

    for (let j = 0; j < timeKeys.length; j++) {
      const time = timeKeys[j];
      const entry = entries[time];

      // Verifica che l'entry esista prima di processarla
      if (!entry) continue;

      const { x, y, cardio, heading, speed, mask } = entry;
      const slotId = getSlotId(parseInt(time));

      if (!slots[slotId]) {
        slots[slotId] = {};
      }

      if (!slots[slotId][playerId]) {
        slots[slotId][playerId] = {};
      }

      // Crea una copia shallow per evitare riferimenti
      slots[slotId][playerId][time] = { x, y, cardio, heading, speed, mask };

      // Libera immediatamente il riferimento
      delete entries[time];

      // Cleanup periodico per evitare accumulo
      if (j % 1000 === 0) {
        // Forza garbage collection ogni 1000 elementi (se disponibile)
        if (global.gc) {
          global.gc();
        }
      }
    }

    // Libera completamente il player processato
    delete data[playerId];
  }

  // Libera il riferimento all'oggetto originale
  playerIds.length = 0;

  return slots;
};
